import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { matchFoundStock } from '../../services/foundStockServices'
import { maxMatchable, unitIdsForMatch } from './foundStockRows'
import { FoundStockDialogShell } from './FoundStockDialogShell'
import { FrozenProposalEventLink } from './FrozenProposalEventLink'
import { useSettlementRequest } from './useSettlementRequest'

const T = 'found_stock.match_dialog'

// A replay payload carries `units[]` instead of the fresh receipt fields; the outcome each unit
// ended in is what actually happened, whoever triggered it.
const countOutcome = (result, outcome) => (result.units || []).filter(u => u.outcome === outcome).length

export const MatchFoundStockDialog = ({
  open,
  warehouseId,
  group,
  product = null,
  onSuccess = () => {},
  onCancel = () => {},
  onGateOff = () => {},
  onStale = () => {},
}) => {
  const { i18n } = useTranslation()
  const cap = maxMatchable(group)
  const [count, setCount] = useState(cap)
  const { busy, errorMessage, errorCoverageEventId, reset, run } = useSettlementRequest({ onSuccess, onGateOff, onStale })

  // Re-seed only when the dialog opens on another declaration or when the cap itself moved (the
  // refetch after INSUFFICIENT_LOST_CANDIDATES): a new group object for the same declaration and
  // the same cap must leave the typed count alone.
  const declarationId = group?.declaration_id
  useEffect(() => {
    if (open) setCount(cap)
    else reset()
  }, [open, declarationId, cap, reset])

  const parsed = Number(count)
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= cap

  const onConfirm = () => {
    if (!valid) return
    run(
      () => matchFoundStock(warehouseId, unitIdsForMatch(group, parsed)),
      result => {
        if (!result.replayed) return i18n.t(`${T}.success`, { count: result.matched })
        const retired = countOutcome(result, 'retired')
        const recovered = countOutcome(result, 'recovered')
        if (retired === 0 && recovered === 0) return i18n.t(`${T}.replayed`)
        return i18n.t(`${T}.replayed_units`, { count: retired, recovered })
      },
    )
  }

  return (
    <FoundStockDialogShell
      open={open}
      title={i18n.t(`${T}.title`)}
      summary={i18n.t(`${T}.summary`, {
        id: group?.declaration_id,
        location: group?.location_code,
        quantity: group?.quantity,
        candidates: group?.match_candidates,
      })}
      product={product}
      productId={group?.product_id}
      busy={busy}
      errorMessage={errorMessage}
      errorAction={<FrozenProposalEventLink warehouseId={warehouseId} eventId={errorCoverageEventId} />}
      confirmLabel={i18n.t(`${T}.confirm`)}
      cancelLabel={i18n.t(`${T}.cancel`)}
      confirmDisabled={!valid}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <p className="text-xs text-gray-500 mb-3">{i18n.t(`${T}.explanation`)}</p>
      <label className="block text-sm font-medium text-gray-700" htmlFor="found-stock-match-count">
        {i18n.t(`${T}.quantity_label`)}
      </label>
      <input
        id="found-stock-match-count"
        type="number"
        min={1}
        max={cap}
        step={1}
        value={count}
        onChange={e => setCount(e.target.value)}
        disabled={busy}
        className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm text-sm"
      />
    </FoundStockDialogShell>
  )
}
